import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import { selectTranslations } from '../../../services/i18n/i18nSlice'
import { setLoading } from '../../../services/global/globalSlice'
import { fetchQuestions } from '../../QuestionBankScreen/QuestionBankApi'

export default function QuestionBankPickerModal({ show, subjects, allTopics, excludedQuestionIds, onAdd, onClose }) {
    const t = useSelector(selectTranslations)
    const dispatch = useDispatch()

    const [subjectFilter, setSubjectFilter] = useState('')
    const [topicFilter, setTopicFilter] = useState('')
    const [typeFilter, setTypeFilter] = useState('')

    const [questions, setQuestions] = useState([])
    const [fetchState, setFetchState] = useState({ start_cursor: null })
    const [selectedQuestions, setSelectedQuestions] = useState({})

    const topicsForFilter = useMemo(() => {
        if (!subjectFilter) return allTopics
        return allTopics.filter(topic => topic.subject_id === subjectFilter)
    }, [subjectFilter, allTopics])

    const topicsById = useMemo(() => {
        const map = {}
        allTopics?.forEach(topic => { map[topic.topic_id] = topic.topic_name })
        return map
    }, [allTopics])

    const subjectNameByTopicId = useMemo(() => {
        const subjectNamesById = {}
        subjects?.forEach(s => { subjectNamesById[s.subject_id] = s.subject_name })
        const map = {}
        allTopics?.forEach(topic => { map[topic.topic_id] = subjectNamesById[topic.subject_id] || '' })
        return map
    }, [allTopics, subjects])

    const filteredQuestions = useMemo(() => {
        if (!typeFilter) return questions
        return questions.filter(q => q.question_type === typeFilter)
    }, [questions, typeFilter])

    const hasActiveFilters = !!(subjectFilter || topicFilter || typeFilter)

    const loadQuestions = async (reset = false) => {
        dispatch(setLoading(true))
        const _params = {
            limit: 20,
            ...(topicFilter && { topic_id: topicFilter }),
            ...(!reset && fetchState?.start_cursor && { start_cursor: fetchState.start_cursor })
        }
        const response = await fetchQuestions(_params)
        if (response.success) {
            setQuestions(reset ? response.data?.questions || [] : [...questions, ...(response.data?.questions || [])])
            setFetchState({ start_cursor: response.data?.next_cursor })
        } else {
            toast.error(t.error.error_loading)
        }
        dispatch(setLoading(false))
    }

    useEffect(() => { if (show) loadQuestions(true) }, [show, topicFilter])

    useEffect(() => {
        if (!show) {
            setSelectedQuestions({})
            setSubjectFilter('')
            setTopicFilter('')
            setTypeFilter('')
        }
    }, [show])

    const handleSubjectFilterChange = (value) => {
        setSubjectFilter(value)
        setTopicFilter('')
    }

    const clearFilters = () => {
        setSubjectFilter('')
        setTopicFilter('')
        setTypeFilter('')
    }

    const toggleQuestion = (question) => {
        if (excludedQuestionIds?.includes(question.question_id)) return
        setSelectedQuestions(prev => {
            const _selected = { ...prev }
            if (_selected[question.question_id]) {
                delete _selected[question.question_id]
            } else {
                _selected[question.question_id] = question
            }
            return _selected
        })
    }

    const selectedCount = Object.keys(selectedQuestions).length

    const handleAdd = () => {
        onAdd(Object.values(selectedQuestions))
    }

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Body>
                <h2 className="text-center">{t.question_bank.bank_picker_title}</h2>
                <div className="d-flex align-items-end flex-wrap mt-3">
                    <Form.Group className="mb-2 me-2" controlId="bank_picker_subject_filter">
                        <Form.Label>{t.question_bank.subject}</Form.Label>
                        <Form.Select value={subjectFilter} onChange={(e) => handleSubjectFilterChange(e.target.value)}>
                            <option value="">{t.question_bank.all_subjects}</option>
                            {subjects?.map(s => (
                                <option key={s.subject_id} value={s.subject_id}>{s.subject_name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-2 me-2" controlId="bank_picker_topic_filter">
                        <Form.Label>{t.question_bank.topic}</Form.Label>
                        <Form.Select value={topicFilter} onChange={(e) => setTopicFilter(e.target.value)}>
                            <option value="">{t.question_bank.all_topics}</option>
                            {topicsForFilter?.map(topic => (
                                <option key={topic.topic_id} value={topic.topic_id}>{topic.topic_name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-2 me-2" controlId="bank_picker_type_filter">
                        <Form.Label>{t.question.question_type}</Form.Label>
                        <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                            <option value="">{t.question_bank.all_types}</option>
                            <option value="base">{t.question.single_choice}</option>
                            <option value="multi">{t.question.multiple_choice}</option>
                            <option value="block">{t.question.block_question}</option>
                            <option value="survey">{t.question.survey_question}</option>
                            <option value="fill">{t.question.fill_question}</option>
                        </Form.Select>
                    </Form.Group>
                    {hasActiveFilters ?
                        <button type="button" className="secondary mb-2" onClick={clearFilters}>{t.question_bank.clear_filters}</button>
                    : null}
                </div>

                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table className="table">
                        <thead>
                            <tr>
                                <th></th>
                                <th>{t.question_bank.question_text}</th>
                                <th>{t.question_bank.subject_topic}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredQuestions?.length ? filteredQuestions.map((q) => {
                                const isExcluded = excludedQuestionIds?.includes(q.question_id)
                                const isSelected = !!selectedQuestions[q.question_id]
                                return (
                                    <tr key={q.question_id} className={isExcluded ? 'text-muted' : ''} style={{ cursor: isExcluded ? 'default' : 'pointer' }} onClick={() => toggleQuestion(q)}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                className="input-check"
                                                checked={isSelected || isExcluded}
                                                disabled={isExcluded}
                                                onChange={() => toggleQuestion(q)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </td>
                                        <td>{q.text}{isExcluded ? ` (${t.question_bank.already_in_module})` : ''}</td>
                                        <td>
                                            <div className="question_bank_tags">
                                                {subjectNameByTopicId[q.topic_id] ?
                                                    <span className="question_bank_tag question_bank_tag_subject">{subjectNameByTopicId[q.topic_id]}</span>
                                                : null}
                                                {topicsById[q.topic_id] ?
                                                    <span className="question_bank_tag question_bank_tag_topic">{topicsById[q.topic_id]}</span>
                                                : null}
                                            </div>
                                        </td>
                                    </tr>
                                )
                            }) : (
                                <tr>
                                    <td colSpan={3} className="text-center">{t.question_bank.no_questions}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {fetchState?.start_cursor ?
                    <div className="text-center">
                        <button type="button" className="secondary" onClick={() => loadQuestions(false)}>{t.question_bank.load_more}</button>
                    </div>
                : null}

                <div className="d-flex justify-content-between align-items-center mt-3">
                    <span>{selectedCount} {t.question_bank.selected_count}</span>
                    <div>
                        <button type="button" disabled={selectedCount === 0} onClick={handleAdd}>{t.question_bank.add_selected}</button>
                        <button type="button" className="ms-2 secondary" onClick={onClose}>{t.modals.abort}</button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    )
}
